import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Standing } from "../../models/Standing";
import {
  getDivisionStandings,
  getDivisionsBySeason,
  getOldestSeason,
} from "../../services/MatchService";
import { currentSeason, divisionMappings } from "../../Constants";
import Loading from "../../components/Loading";
import {
  QualifiedTeamsGroup,
  QualifierBracket as QualifierBracketData,
  getQualifiedTeams,
  getQualifierBrackets,
} from "../../services/TournamentService";
import { useDarkMode } from "../../hooks/useDarkmode";
import SiteBanner from "../../components/SiteBanner/SiteBanner";
import QualifierBracket from "../../components/Bracket/QualifierBracket";
import styles from "../../styles/Tables.module.css";
import { getStandingZone } from "../../utils/standingZones";

type StandingsView = 'groupStage' | 'qualifiers';

interface DivisionStandings {
  [division: string]: Standing[];
}

// Color palette - softer version of the original colors
const colorPalette = {
  light: {
    green: "rgba(135, 245, 135, 0.4)", // lighter pastel green
    yellow: "rgba(250, 250, 178, 0.83)", // softer light yellow
    orange: "rgba(248, 203, 164, 0.6)", // peach/light orange
    red: "rgba(250, 169, 182, 0.5)",    // light pink-red
    white: "transparent",
    tableBorder: "#dee2e6",
    textColor: "#212529",
    headerBg: "#f8f9fa",
    rowEven: "#F2F2F2",
    rowOdd: "#FFFFFF"
  },
  dark: {
    green: "rgba(0, 128, 0, 0.3)",     // dark green with transparency
    yellow: "rgba(204, 204, 0, 0.25)", // darker yellow with transparency
    orange: "rgba(210, 105, 30, 0.3)", // dark orange with transparency
    red: "rgba(139, 0, 0, 0.3)",       // dark red with transparency
    white: "transparent",
    tableBorder: "#4a5568",
    textColor: "#e2e8f0",
    headerBg: "#333",  // Match leaderboard table header
    rowEven: "#2a2a2a",
    rowOdd: "#242424"
  }
};

const StandingPage = () => {
  const { t } = useTranslation(['pages', 'common', 'stats', 'errors']);

  // Read :season and :division from the URL (if provided)
  const { season, division } = useParams<{ season: string; division: string }>();

  // For storing all the data we need
  const [standings, setStandings] = useState<DivisionStandings>({});
  const [selectedSeason, setCurrentSeason] = useState<number>(
    season ? Number(season) : currentSeason
  );
  const [seasons, setSeasons] = useState<number[]>([]);
  const [divisions, setDivisions] = useState<
    { division: string; formattedDivision: string }[]
  >([]);
  const [currentDivision, setCurrentDivision] = useState<string>(division || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qualifiedGroups, setQualifiedGroups] = useState<QualifiedTeamsGroup[]>([]);
  const [brackets, setBrackets] = useState<QualifierBracketData[]>([]);
  // Null until both standings and brackets have loaded, so the default can be chosen from data.
  const [view, setView] = useState<StandingsView | null>(null);
  
  const isDark = useDarkMode();

  // Get current color theme
  const colors = isDark ? colorPalette.dark : colorPalette.light;
  
  // Memoize the current division name to avoid repeated lookups
  const currentDivisionName = useMemo(() => {
    return currentDivision
      ? divisions.find((d) => d.division === currentDivision)?.formattedDivision
      : t('common:all');
  }, [currentDivision, divisions, t]);

  /**
   * Load all possible seasons (from oldest to latest).
   * If no season param is in the URL, default to the latest.
   */
  useEffect(() => {
    const loadSeasons = async () => {
      setLoading(true);
      try {
        const oldest = await getOldestSeason();
        const seasonRange = Array.from(
          { length: currentSeason - oldest + 1 },
          (_, i) => oldest + i
        ).sort((a, b) => b - a); // descending
        setSeasons(seasonRange);

        if (!season) {
          // If the user didn't provide a season in URL, default to 'latest'
          setCurrentSeason(currentSeason);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    };
    loadSeasons();
  }, [season]);

  /**
   * Utility to create a division object that contains both the "raw" division
   * (e.g. "2divA") and a "formatted" version (e.g. "2. Division A").
   */
  const createDivision = useCallback(
    (divisionPrefix: string, divisionName: string) => {
      const isLigaen = divisionName === "Ligaen";
      // "Ligaen" is left as-is, otherwise we split it up
      const formattedDivision = isLigaen
        ? divisionName
        : divisionName.split(" ", 2).join(" ");
      return {
        division: divisionPrefix,
        formattedDivision,
      };
    },
    []
  );

  /**
   * Fetch the standings for the current season + current division (if any).
   */
  useEffect(() => {
    const fetchStandings = async () => {
      setLoading(true);
      try {
        const standingsData = await getDivisionStandings(selectedSeason, currentDivision);
        setStandings(standingsData as DivisionStandings);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    };
  
    fetchStandings();
  }, [currentDivision, selectedSeason]);

  // Fetch qualified teams from kval tournaments
  useEffect(() => {
    getQualifiedTeams(selectedSeason).then(setQualifiedGroups).catch(() => setQualifiedGroups([]));
  }, [selectedSeason]);

  // Qualifier brackets, derived server-side from the cached OverPass payload.
  useEffect(() => {
    getQualifierBrackets(selectedSeason).then(setBrackets).catch(() => setBrackets([]));
  }, [selectedSeason]);

  // Reset the tab choice whenever the season changes so the default is re-evaluated.
  useEffect(() => {
    setView(null);
  }, [selectedSeason]);

  const hasGroupStage = useMemo(
    () => Object.values(standings).some((rows) => rows && rows.length > 0),
    [standings]
  );
  // An empty qualifier shell (season 33 ships four, only one played) must not make the tab appear.
  const hasBrackets = brackets.some((b) =>
    b.sections.some((section) => section.rounds.some((round) => round.matches.length > 0))
  );

  /**
   * Early in a season only the qualifiers have been played, so those are what people come to
   * look at. Once group-stage games exist that becomes the main event and we always land there.
   */
  const activeView: StandingsView =
    view ?? (hasGroupStage || !hasBrackets ? 'groupStage' : 'qualifiers');

  /**
   * Load all divisions for the selected season
   */
  useEffect(() => {
    const loadDivisions = async () => {
      setLoading(true);
      try {
        let divisions = await getDivisionsBySeason(selectedSeason);

        // Filter out "playoff" as it's not a real division
        divisions = divisions.filter(div => div.toLowerCase() !== 'playoff');

        const divisionMap = new Map<string, { division: string; formattedDivision: string }>();
        divisions.forEach((divisionValue) => {
          const formattedDivision = divisionMappings.get(divisionValue) || divisionValue;

          if (divisionValue === "ligaen" || divisionValue === "1div") {
            divisionMap.set(
              divisionValue,
              createDivision(divisionValue, formattedDivision)
            );
          }

          const divisionAndGroup = divisionValue.match(/^(\d+div)([A-Z])?$/);
          if (divisionAndGroup) {
            const [divisionPrefix] = divisionAndGroup.slice(1);
            divisionMap.set(
              divisionPrefix,
              createDivision(divisionPrefix, formattedDivision)
            );
          }
        });

        const divisionArray = Array.from(divisionMap.values());
        // Sort by division hierarchy: ligaen (if exists) -> 1div -> 2div -> etc.
        const getOrder = (div: string) => {
          if (div === 'ligaen') return 0;
          if (div === '1div') return 1;
          if (div === '2div') return 2;
          if (div === '3div') return 3;
          if (div === '4div') return 4;
          if (div === '5div') return 5;
          return 6;
        };
        divisionArray.sort((a, b) => getOrder(a.division) - getOrder(b.division));
        setDivisions(divisionArray);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    };
    loadDivisions();
  }, [createDivision, selectedSeason]);

  /**
   * Decide the color for each row in the standings table (promotion/relegation).
   */
  const getStandingColor = (divisionKey: string, index: number, standing?: Standing) =>
    getStandingZone(selectedSeason, divisionKey, index, standing);

  // Get the actual color from the color palette based on dark mode
  const getColorValue = (colorName: string) => {
    return colors[colorName as keyof typeof colors] || "transparent";
  };

  /**
   * Renders the standings table for each division
   */
  const renderStandings = () => {
    if (loading) {
      return <Loading />;
    }

    if (error) {
      return (
        <div className="text-center">
          <h1>{t('errors:generic.somethingWentWrong')}</h1>
          <p>{error}</p>
        </div>
      );
    }

    // Render each division's table, filtering out empty divisions
    const divisionEntries = Object.entries(standings).filter(
      ([_, divisionStandings]) => divisionStandings && divisionStandings.length > 0
    );
    const singleDivision = divisionEntries.length === 1;

    return (
      <div className="row">
        {divisionEntries.map(([divisionKey, divisionStandings]) => (
          <div 
            key={divisionKey} 
            className={`col-12 ${singleDivision ? 'col-md-8 mx-auto' : 'col-md-6'} mb-4`}
          >
            {divisionStandings && (
              <>
                <div className={`${styles.tableContainer} ${isDark ? styles.dark : ''}`}>
                  <div className={`${styles.cardHeader} ${isDark ? styles.dark : ''}`}>
                    <h4 className={`${styles.cardTitle} ${isDark ? styles.dark : ''}`}>
                      {divisionMappings.get(divisionKey)}
                    </h4>
                  </div>
                  <div className={styles.container}>
                    <table className={styles.table}>
                    <thead>
                      <tr className={`${styles.tableHeader} ${isDark ? styles.dark : ''}`}>
                        <th className={`${styles.tableHeaderCell} ${styles.center} ${isDark ? styles.dark : ''}`} style={{ width: "40px" }}>
                          #
                        </th>
                        <th className={`${styles.tableHeaderCell} ${isDark ? styles.dark : ''}`}>
                          {t('common:team')}
                        </th>
                        {divisionKey === "ligaen" ? (
                          <>
                            <th className={`${styles.tableHeaderCell} ${styles.center} ${isDark ? styles.dark : ''}`} style={{ width: "70px" }}>
                              Kampe
                            </th>
                            <th className={`${styles.tableHeaderCell} ${styles.center} ${isDark ? styles.dark : ''}`} style={{ width: "70px" }}>
                              Vundet
                            </th>
                            <th className={`${styles.tableHeaderCell} ${styles.center} ${isDark ? styles.dark : ''}`} style={{ width: "70px" }}>
                              Tabt
                            </th>
                          </>
                        ) : (
                          <>
                            <th className={`${styles.tableHeaderCell} ${styles.center} ${isDark ? styles.dark : ''}`}>
                              {t('stats:matchesWL')}
                            </th>
                            <th className={`${styles.tableHeaderCell} ${styles.right} ${isDark ? styles.dark : ''}`} style={{ width: "80px" }}>
                              Score
                            </th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {divisionStandings.map((standing, index) => {
                        const colorKey = getStandingColor(divisionKey, index, standing);
                        const colorValue = getColorValue(colorKey);
                        // Link to /team/:teamId?season=selectedSeason
                        const teamLink = `/team/${standing.team.teamId}?season=${selectedSeason}`;
                        const rowBackground = index % 2 === 0 ? colors.rowEven : colors.rowOdd;

                        return (
                          <tr 
                            key={standing.team.teamId}
                            className={`${styles.tableRow} ${styles.standings} ${isDark ? styles.dark : ''}`}
                            style={{ backgroundColor: rowBackground }}
                          >
                            <td 
                              className={`${styles.tableCell} ${styles.compact} ${styles.center} ${styles.bold}`}
                              style={{ 
                                backgroundColor: colorValue,
                                borderRadius: "4px",
                                width: "40px",
                                border: colorKey !== "white" ? `1px solid ${colorValue}` : "none"
                              }}
                            >
                              {standing.rank}
                            </td>
                            <td className={`${styles.tableCell} ${styles.compact}`}>
                              <Link
                                to={teamLink}
                                className={`${styles.tableLink} ${isDark ? styles.dark : styles.light}`}
                              >
                                <img
                                  src={standing.team.teamLogo}
                                  alt={standing.team.teamName}
                                  className={styles.teamLogo}
                                  style={{ width: 25, marginRight: "8px" }}
                                />
                                <span>{standing.team.teamName}</span>
                              </Link>
                            </td>
                            {divisionKey === "ligaen" ? (
                              <>
                                <td className={`${styles.tableCell} ${styles.compact} ${styles.center}`}>
                                  {standing.gamesPlayed}
                                </td>
                                <td className={`${styles.tableCell} ${styles.compact} ${styles.center}`}>
                                  {standing.wins}
                                </td>
                                <td className={`${styles.tableCell} ${styles.compact} ${styles.center}`}>
                                  {standing.gamesPlayed - standing.wins}
                                </td>
                              </>
                            ) : (
                              <>
                                <td className={`${styles.tableCell} ${styles.compact} ${styles.center}`}>
                                  {standing.wins}/{standing.gamesPlayed - standing.wins}
                                </td>
                                <td className={`${styles.tableCell} ${styles.compact} ${styles.right} ${styles.bold}`}>
                                  {standing.points}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={isDark ? 'text-white' : ''}>
      <SiteBanner />
      <div className="text-center">
        <h1>{t('pages:standings.title')}</h1>
        <p>{t('pages:standings.description')}</p>
      </div>
      <hr style={{ borderColor: isDark ? '#555' : '#ccc' }} />

      {/* Season and Division dropdowns side by side */}
      <div className="row justify-content-center text-center mb-3">
        <div className="col-6 col-md-3">
          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t('common:selectSeason')}</span>
          <div className="dropdown">
            <button
              className="btn dropdown-toggle w-100"
              type="button"
              id="seasonDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(255, 102, 0, 0.2)',
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease'
              }}
            >
              {t('common:seasonNum', { number: selectedSeason })}
            </button>
            <ul
              className="dropdown-menu text-center w-100"
              style={{
                background: 'rgba(26, 26, 26, 0.95)',
                border: '1px solid rgba(255, 102, 0, 0.2)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
              aria-labelledby="seasonDropdown"
            >
              {seasons.map((s) => (
                <li key={s}>
                  <button
                    className="dropdown-item"
                    onClick={() => setCurrentSeason(s)}
                    style={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                      e.currentTarget.style.color = '#ff9933';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                    }}
                  >
                    {t('common:seasonNum', { number: s })}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="col-6 col-md-3">
          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t('common:selectDivision')}</span>
          <div className="dropdown">
            <button
              className="btn dropdown-toggle w-100"
              type="button"
              id="divisionDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(255, 102, 0, 0.2)',
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease'
              }}
            >
              {currentDivisionName}
            </button>
            <ul
              className="dropdown-menu text-center w-100"
              style={{
                background: 'rgba(26, 26, 26, 0.95)',
                border: '1px solid rgba(255, 102, 0, 0.2)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
              aria-labelledby="divisionDropdown"
            >
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => setCurrentDivision("")}
                  style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                    e.currentTarget.style.color = '#ff9933';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                  }}
                >
                  {t('common:all')}
                </button>
              </li>
              {divisions.map((d) => (
                <li key={d.division}>
                  <button
                    className="dropdown-item"
                    onClick={() => setCurrentDivision(d.division)}
                    style={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                      e.currentTarget.style.color = '#ff9933';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                    }}
                  >
                    {d.formattedDivision}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Group stage / qualifiers switch — only shown when there are qualifiers to switch to */}
      {hasBrackets && (
        <div className="d-flex justify-content-center gap-2 mb-4">
          {(['groupStage', 'qualifiers'] as StandingsView[]).map((option) => (
            <button
              key={option}
              type="button"
              className="btn"
              onClick={() => setView(option)}
              style={{
                background: activeView === option
                  ? 'linear-gradient(135deg, #ff6600, #ff9933)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: `1px solid ${activeView === option ? 'transparent' : 'rgba(255, 102, 0, 0.2)'}`,
                borderRadius: '8px',
                color: activeView === option ? '#fff' : 'rgba(255, 255, 255, 0.75)',
                fontSize: '0.8rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                minWidth: '160px',
                padding: '8px 20px',
                textTransform: 'uppercase',
              }}
            >
              {t(`pages:standings.${option}`)}
            </button>
          ))}
        </div>
      )}

      {/* Legend — only meaningful for the group-stage table */}
      {activeView === 'groupStage' && (
      <div className="row justify-content-center mb-4">
        <div className="col-12 col-md-8">
          <div className="d-flex flex-wrap justify-content-center gap-3">
            <div className="d-flex align-items-center">
              <div style={{ 
                width: "20px", 
                height: "20px", 
                backgroundColor: colors.green,
                borderRadius: "4px",
                marginRight: "8px"
              }}></div>
              <span>{t('pages:standings.promotion')}</span>
            </div>
            <div className="d-flex align-items-center">
              <div style={{
                width: "20px",
                height: "20px",
                backgroundColor: colors.yellow,
                borderRadius: "4px",
                marginRight: "8px"
              }}></div>
              <span>{t('pages:standings.playoff')}</span>
            </div>
            <div className="d-flex align-items-center">
              <div style={{
                width: "20px",
                height: "20px",
                backgroundColor: colors.orange,
                borderRadius: "4px",
                marginRight: "8px"
              }}></div>
              <span>{t('pages:standings.relegationPlayoff')}</span>
            </div>
            <div className="d-flex align-items-center">
              <div style={{
                width: "20px",
                height: "20px",
                backgroundColor: colors.red,
                borderRadius: "4px",
                marginRight: "8px"
              }}></div>
              <span>{t('pages:standings.relegation')}</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Qualified teams from kval tournaments */}
      {qualifiedGroups.length > 0 && !standings["ligaen"]?.length && (currentDivision === "" || currentDivision === "ligaen") && (
        <div className="mb-4">
          <h5 style={{ color: colors.textColor, marginBottom: 16 }}>Kvalificerede Hold</h5>
          <div className="row">
            {qualifiedGroups.map((group) => (
              <div key={group.divisionName} className="col-md-6 mb-3">
                <div style={{
                  background: isDark ? "linear-gradient(135deg, #1a1a1a, #252525)" : "#fff",
                  border: `1px solid ${isDark ? "rgba(255,102,0,0.15)" : colors.tableBorder}`,
                  borderRadius: 12,
                  overflow: "hidden"
                }}>
                  <div style={{
                    padding: "12px 16px",
                    background: isDark ? "linear-gradient(135deg, #2a2a2a, #333)" : colors.headerBg,
                    borderBottom: `2px solid ${isDark ? "rgba(255,102,0,0.15)" : colors.tableBorder}`,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    color: isDark ? "rgba(255,255,255,0.9)" : colors.textColor
                  }}>
                    {group.displayName}
                  </div>
                  {group.teams.map((team, idx) => (
                    <Link
                      key={team.teamId}
                      to={`/team/${team.teamId}?season=${selectedSeason}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 16px",
                        textDecoration: "none",
                        color: isDark ? "#e2e8f0" : colors.textColor,
                        background: idx % 2 === 0
                          ? (isDark ? "rgba(42,42,42,0.5)" : "transparent")
                          : (isDark ? "rgba(36,36,36,0.5)" : colors.rowEven),
                        transition: "background 0.2s"
                      }}
                    >
                      <span style={{ color: isDark ? "rgba(255,255,255,0.4)" : "#999", width: 20, textAlign: "center", fontWeight: 500 }}>{idx + 1}</span>
                      {team.teamLogo && <img src={team.teamLogo} alt="" style={{ width: 24, height: 24, objectFit: "contain" }} />}
                      <span style={{ fontWeight: 500 }}>{team.teamName}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeView === 'groupStage'
        ? renderStandings()
        : brackets.map((bracket) => (
            <QualifierBracket key={bracket.divisionName} bracket={bracket} />
          ))}
    </div>
  );
};

export default StandingPage;